import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Swords, Trophy, Crown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { LandingNav } from "@/components/landing/LandingNav";
import { Footer } from "@/components/Footer";
import { PublicSkeleton } from "@/components/Skeletons";
import { ClipTile } from "@/pages/CreatorProfile";
import { ChallengeHero, fmtDate } from "@/components/challenges/ChallengeHero";
import { EnterDialog } from "@/components/challenges/EnterDialog";

const Participate = ({ c, mine, onChange }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (c.status !== "active") return null;
  if (!user) return <Link to="/login" className="cx-btn rounded-full px-6 py-3 text-sm font-semibold inline-block mt-6" data-testid="challenge-login-btn">{t("ch.login")}</Link>;
  const withdraw = async () => {
    try { await api.delete(`/challenges/${c.id}/enter`); toast.success(t("ch.withdrawn")); onChange(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 mt-6">
      {mine && <span className="text-sm text-[#22e3a1]" data-testid="challenge-my-entry">{t("ch.my_entry")}</span>}
      <button onClick={() => setOpen(true)} className="cx-btn rounded-full px-6 py-3 text-sm font-semibold" data-testid="challenge-enter-btn">{mine ? t("ch.change") : t("ch.enter")}</button>
      {mine && <button onClick={withdraw} className="cx-ghost rounded-full px-5 py-3 text-sm text-slate-300" data-testid="challenge-withdraw-btn">{t("ch.withdraw")}</button>}
      <EnterDialog open={open} onClose={() => setOpen(false)} challengeId={c.id} current={mine} onDone={onChange} />
    </div>
  );
};

const Ranking = ({ entries, mineSid }) => {
  const { t } = useI18n();
  if (!entries.length) return <p className="text-slate-500 mt-6" data-testid="challenge-no-entries">{t("ch.no_entries")}</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 mt-6" data-testid="challenge-ranking">
      {entries.map((g, i) => (
        <div key={g.share_id} className={mineSid === g.share_id ? "rounded-3xl ring-2 ring-[#22e3a1]/60 ring-offset-4 ring-offset-[#04050a]" : ""}>
          <div className="flex items-center justify-between mb-2 px-1" data-testid={`challenge-rank-${i + 1}`}>
            <span className={`font-display font-bold text-lg flex items-center gap-1 ${i === 0 ? "text-[#ffc94d]" : i < 3 ? "text-[#c79bff]" : "text-slate-500"}`}>
              {i === 0 && <Crown className="w-4 h-4" />}#{g.rank}
            </span>
            <span className="text-xs font-mono text-[#ff4d6d]" data-testid={`challenge-score-${i + 1}`}>{t((g.score) === 1 ? "ch.score_one" : "ch.score").replace("{n}", g.score)}</span>
          </div>
          <ClipTile g={g} i={i} prefix="challenge" />
        </div>
      ))}
    </div>
  );
};

const PastList = ({ items, current, onPick }) => {
  const { t, lang } = useI18n();
  if (!items.length) return null;
  return (
    <section className="mt-16" data-testid="challenge-past">
      <h3 className="font-display text-xl text-white flex items-center gap-2"><Trophy className="w-5 h-5 text-[#ffc94d]" />{t("ch.past")}</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
        {items.map((c) => (
          <button key={c.id} onClick={() => onPick(c.id)} data-testid={`challenge-past-${c.id}`}
            className={`cx-card rounded-2xl p-5 text-left transition-colors ${current === c.id ? "border-[#ff4d6d]/60" : "hover:border-white/20"}`}>
            <div className="text-white font-semibold truncate">{c.title}</div>
            <div className="text-xs text-slate-500 mt-1">{fmtDate(c.ends_at, lang)}</div>
            <div className="text-sm mt-3 text-[#ffc94d] truncate">{c.winner ? `${t("ch.winner")} : ${c.winner.handle ? "@" + c.winner.handle : c.winner.creator}` : <span className="text-slate-500">{t("ch.no_winner")}</span>}</div>
          </button>
        ))}
      </div>
    </section>
  );
};

export default function Challenges() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [mine, setMine] = useState({});
  useEffect(() => {
    api.get("/challenges").then((r) => { setList(r.data); const f = r.data.active[0] || r.data.upcoming[0] || r.data.past[0]; setSel(f ? f.id : ""); })
      .catch(() => setList({ active: [], upcoming: [], past: [] }));
  }, []);
  const load = useCallback(() => {
    if (!sel) return;
    api.get(`/challenges/${sel}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
    if (user) api.get(`/challenges/${sel}/mine`).then((r) => setMine(r.data)).catch(() => setMine({}));
  }, [sel, user]);
  useEffect(() => { load(); }, [load]);
  if (!list || user === null) return <PublicSkeleton testid="challenges-loading" />;
  const tabs = [...list.active, ...list.upcoming];
  const body = (
    <>
        <span className="cx-label flex items-center gap-2"><Swords className="w-4 h-4 text-[#ff4d6d]" />{t("ch.label")}</span>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-3"><span className="cx-chrome">{t("ch.title1")}</span><span className="cx-text-grad">{t("ch.title2")}</span></h1>
        <p className="text-slate-400 mt-4 max-w-2xl">{t("ch.sub")}</p>
        {tabs.length > 1 && (
          <div className="flex flex-wrap gap-2 mt-8" data-testid="challenge-tabs">
            {tabs.map((c) => (
              <button key={c.id} onClick={() => setSel(c.id)} data-testid={`challenge-tab-${c.id}`}
                className={`rounded-full px-4 py-2 text-xs border transition-colors ${sel === c.id ? "bg-[#ff4d6d]/15 border-[#ff4d6d]/60 text-white" : "border-white/10 text-slate-400 hover:text-white"}`}>{c.title}</button>
            ))}
          </div>
        )}
        <div className="mt-8">
          {!sel && <p className="text-slate-400 py-16 text-center cx-card rounded-3xl" data-testid="challenges-none">{t("ch.none")}</p>}
          {detail && (<>
            <ChallengeHero c={detail}><Participate c={detail} mine={mine.clip_id} onChange={load} /></ChallengeHero>
            {detail.status !== "upcoming" && (<>
              <h3 className="font-display text-xl text-white mt-12">{t("ch.ranking")}</h3>
              <p className="text-xs text-slate-500 mt-1">{t("ch.vote_hint")} {t("ch.rules")}</p>
              <Ranking entries={detail.entries} mineSid={mine.share_id} />
            </>)}
          </>)}
        </div>
        <PastList items={list.past} current={sel} onPick={setSel} />
    </>
  );
  if (user) return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-7xl text-white" data-testid="challenges-page" data-mode="app">{body}</div>
    </Layout>
  );
  return (
    <div className="min-h-screen bg-[#04050a] text-white" data-testid="challenges-page" data-mode="public">
      <LandingNav />
      <main className="max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20">{body}</main>
      <Footer />
    </div>
  );
}
